import { useState, useCallback } from 'react';

export type ServerPaginationInit = {
  initialPage?: number;
  initialLastKeys?: (string | null)[];
};

export function useServerPagination(
  initialPageSize: number = 50,
  init?: ServerPaginationInit
) {
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [currentPage, setCurrentPage] = useState(() =>
    Math.max(0, init?.initialPage ?? 0)
  );
  const [lastKeys, setLastKeys] = useState<(string | null)[]>(() => {
    const keys = init?.initialLastKeys;
    if (keys && keys.length > 0) {
      return [...keys];
    }
    return [null];
  });

  // Get the lastKey needed to fetch the current page
  const currentLastKey = lastKeys[currentPage] ?? null;

  const goToPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // Store the lastKey for the next page when we receive a response
  const storeNextPageKey = useCallback((nextPageLastKey: string | null) => {
    setLastKeys((prev) => {
      const newKeys = [...prev];
      const nextPageIndex = currentPage + 1;
      newKeys[nextPageIndex] = nextPageLastKey;
      return newKeys;
    });
  }, [currentPage]);

  const nextPage = useCallback(() => {
    const nextPageIndex = currentPage + 1;
    // Check if we have a lastKey for the next page
    const nextPageKey = lastKeys[nextPageIndex];
    if (nextPageKey !== undefined) {
      goToPage(nextPageIndex);
    }
  }, [currentPage, lastKeys, goToPage]);

  const previousPage = useCallback(() => {
    if (currentPage > 0) {
      goToPage(currentPage - 1);
    }
  }, [currentPage, goToPage]);

  const reset = useCallback(() => {
    setCurrentPage(0);
    setLastKeys([null]);
  }, []);

  const hydrate = useCallback((page: number, keys: (string | null)[]) => {
    const next = keys.length > 0 ? [...keys] : [null];
    const maxPage = Math.max(0, next.length - 1);
    setCurrentPage(Math.min(Math.max(0, page), maxPage));
    setLastKeys(next);
  }, []);

  const getLastKeyForPage = useCallback((page: number): string | null => {
    return lastKeys[page] ?? null;
  }, [lastKeys]);

  return {
    currentPage,
    pageSize,
    currentLastKey,
    lastKeys,
    setPageSize,
    goToPage,
    nextPage,
    previousPage,
    reset,
    hydrate,
    storeNextPageKey,
    getLastKeyForPage,
    canGoNext: lastKeys[currentPage + 1] !== undefined && lastKeys[currentPage + 1] !== null,
    canGoPrevious: currentPage > 0,
  };
}
